import React, { useState } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useQuery } from 'react-query';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { auteursAPI } from '../../services/api';

// Fonction de normalisation pour la comparaison d'auteurs
const normalizeString = (str) => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

function AuteurSelector({ control, name, disabled = false, label = "Auteurs" }) {
  const { t } = useTranslation();
  const [auteurSearchTerm, setAuteurSearchTerm] = useState('');

  // Recherche d'auteurs
  const { data: auteursData, isLoading: auteursLoading } = useQuery(
    ['auteurs-list', auteurSearchTerm],
    () => auteursAPI.search(auteurSearchTerm).then(res => res.data.data),
    {
      enabled: auteurSearchTerm.length >= 2,
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000
    }
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
          <Autocomplete
            {...field}
            multiple
            freeSolo
            options={auteursData || []}
            getOptionLabel={(option) => {
              // Pour les options d'ajout
              if (option.isAddOption) {
                return t('auteurSelector.addOption', { value: option.inputValue });
              }
              // Pour les nouvelles valeurs string
              if (typeof option === 'string') return option;
              // Pour les objets avec displayName
              if (option.displayName) return option.displayName;
              // Pour les auteurs existants
              return `${option.prenom || ''} ${option.nom}`.trim();
            }}
            isOptionEqualToValue={(option, value) => {
              if (typeof option === 'object' && typeof value === 'object') {
                if (option.id && value.id) return option.id === value.id;
                if (option.isNew && value.isNew) return option.displayName === value.displayName;
              }
              return false;
            }}
            filterOptions={(options, params) => {
              const normalizedInput = normalizeString(params.inputValue);
              
              const filtered = options.filter(option => {
                const nomComplet = `${option.prenom || ''} ${option.nom}`.trim();
                return normalizeString(nomComplet).includes(normalizedInput);
              });

              const { inputValue } = params;
              // Suggérer l'ajout d'un nouvel auteur si la saisie ne correspond à aucun existant
              const isExisting = options.some(option => {
                const nomComplet = `${option.prenom || ''} ${option.nom}`.trim();
                return normalizeString(nomComplet) === normalizedInput;
              });
              
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  inputValue,
                  nom: inputValue,
                  prenom: '',
                  displayName: inputValue,
                  isNew: true,
                  isAddOption: true
                });
              }

              return filtered;
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const tagProps = getTagProps({ index });
                
                return (
                  <Chip
                    key={option.id || option.displayName || index}
                    variant={option.isNew ? "filled" : "outlined"}
                    color={option.isNew ? "secondary" : "default"}
                    label={
                      typeof option === 'string' ? option :
                      option.displayName ? 
                        (option.isNew ? `${option.displayName} (${t('auteurSelector.new')})` : option.displayName) :
                      `${option.prenom || ''} ${option.nom}`.trim()
                    }
                    {...tagProps}
                    onClick={(e) => {
                      // Empêcher la suppression du chip lors du clic
                      e.stopPropagation();
                      
                      // Obtenir le texte à éditer
                      const textToEdit = typeof option === 'string' ? option :
                        option.displayName || `${option.prenom || ''} ${option.nom}`.trim();
                      
                      // Mettre le texte dans le champ de recherche
                      setAuteurSearchTerm(textToEdit);
                      
                      // Supprimer ce chip de la liste
                      const newValue = [...field.value];
                      newValue.splice(index, 1);
                      field.onChange(newValue);
                      
                      console.log('✏️ Édition du chip d\'auteur:', textToEdit);
                    }}
                    onDelete={tagProps.onDelete}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: option.isNew ? 'secondary.light' : 'action.hover',
                      }
                    }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder={auteursLoading ? t('auteurSelector.loading') : t('auteurSelector.placeholder')}
                margin="none"
                helperText={t('auteurSelector.helperText')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {auteursLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            onChange={(event, newValue, reason, details) => {
              console.log('🔄 AuteurSelector onChange:', { newValue, reason, details });
              
              // Si on ajoute par freeSolo (entrée au clavier)
              if (reason === 'createOption' && details?.option && typeof details.option === 'string') {
                const inputValue = details.option.trim();
                const [prenom, ...nomParts] = inputValue.split(' ');
                const nom = nomParts.join(' ') || prenom;
                const prenomFinal = nomParts.length > 0 ? prenom : '';
                
                const newAuteur = {
                  isNew: true,
                  nom: nom,
                  prenom: prenomFinal,
                  displayName: inputValue
                };
                
                const updatedValue = [...(field.value || []), newAuteur];
                field.onChange(updatedValue);
                console.log('✅ Nouvel auteur ajouté via freeSolo:', newAuteur);
                return;
              }
              
              // Traiter les autres cas
              const processedAuteurs = newValue.map(option => {
                if (typeof option === 'string') {
                  // Nouveau auteur saisi directement
                  const [prenom, ...nomParts] = option.trim().split(' ');
                  const nom = nomParts.join(' ') || prenom;
                  const prenomFinal = nomParts.length > 0 ? prenom : '';
                  
                  return {
                    isNew: true,
                    nom: nom,
                    prenom: prenomFinal,
                    displayName: option.trim()
                  };
                } else if (typeof option === 'object') {
                  if (option.id) {
                    // Auteur existant
                    return option;
                  } else if (option.isNew) {
                    // Nouvel auteur via l'option "Ajouter"
                    return option;
                  }
                }
                return null;
              }).filter(Boolean);
              
              field.onChange(processedAuteurs);
              console.log('✅ Auteurs sélectionnés:', processedAuteurs);
            }}
            onInputChange={(event, newInputValue) => {
              setAuteurSearchTerm(newInputValue);
            }}
            inputValue={auteurSearchTerm}
            disabled={disabled}
          />
      )}
    />
  );
}

export default AuteurSelector;
